/**
 * UranIA PA — API client onboarding R&I (US-016 AC#2/AC#3).
 *
 * Wrapper su `fetch` per `services/api-gateway` (OAS v0.4.0):
 *   - `POST /api/v1/onboarding/ri/sessions`              -> apre/idempot.
 *   - `POST /api/v1/onboarding/ri/sessions/{id}/steps/{n}` -> persist step.
 *
 * Auth (US-014 backend + US-010 SPA): bearer JWT preso dallo Zustand
 * `useAuthStore.accessToken` (refresh silente via Keycloak SSO cookie,
 * niente refresh_token client-side per FR-F1-10).
 *
 * Errori:
 *   - rete / 5xx -> `OnboardingApiError` con codice `network_error`;
 *   - 4xx Problem+JSON RFC 9457 -> `OnboardingApiError` con `status` +
 *     `code` (campo OAS `Problem.code` enum: `session_not_found`,
 *     `tenant_mismatch`, `step_out_of_order`, `step_prerequisites_missing`,
 *     ...).
 *
 * Riusa la classe `OnboardingApiError` di `pa-api.ts` per uniformare
 * l'handling nei consumer (medesima shape `Problem.code`).
 */

import { loadSpaEnv } from '../env.ts';
import { useAuthStore } from '../auth/auth-store.ts';
import { OnboardingApiError } from './pa-api.ts';
import type {
  OnboardingRiSessionDto,
  OnboardingRiStep1Body,
  OnboardingRiStep2Body,
  OnboardingRiStep3Body,
  OnboardingRiStep4Body,
  OnboardingRiStep5Body,
  OnboardingRiStepNumber,
  OnboardingRiStepResponseDto,
} from './ri-schemas.ts';

export type OnboardingRiStepBody =
  | OnboardingRiStep1Body
  | OnboardingRiStep2Body
  | OnboardingRiStep3Body
  | OnboardingRiStep4Body
  | OnboardingRiStep5Body;

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

interface ProblemBody {
  readonly title?: unknown;
  readonly detail?: unknown;
  readonly status?: unknown;
  readonly code?: unknown;
  readonly acr_required?: unknown;
}

function readStringArray(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter((v): v is string => typeof v === 'string');
}

async function parseProblem(response: Response): Promise<OnboardingApiError> {
  let body: ProblemBody = {};
  try {
    body = (await response.json()) as ProblemBody;
  } catch {
    // Server non ha emesso Problem+JSON valido: fallback su statusText.
  }
  const title =
    typeof body.title === 'string' && body.title.length > 0
      ? body.title
      : response.statusText || 'http_error';
  const detail = typeof body.detail === 'string' ? body.detail : null;
  const code = typeof body.code === 'string' ? body.code : null;
  return new OnboardingApiError({
    status: response.status,
    code,
    title,
    detail,
    acrRequired: readStringArray(body.acr_required),
  });
}

async function postJson(
  path: string,
  body: unknown,
): Promise<{ readonly response: unknown; readonly status: number }> {
  let response: Response;
  const init: RequestInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    credentials: 'include',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  try {
    response = await fetch(buildUrl(path), init);
  } catch (err) {
    throw new OnboardingApiError({
      status: 0,
      code: 'network_error',
      title: err instanceof Error ? err.message : 'network_error',
      detail: null,
    });
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  const parsed: unknown = await response.json();
  return { response: parsed, status: response.status };
}

export async function createOnboardingRiSession(): Promise<{
  readonly session: OnboardingRiSessionDto;
  readonly status: 200 | 201;
}> {
  const { response, status } = await postJson('/api/v1/onboarding/ri/sessions', undefined);
  return { session: response as OnboardingRiSessionDto, status: status === 200 ? 200 : 201 };
}

export async function submitOnboardingRiStep(
  sessionId: string,
  stepNumber: OnboardingRiStepNumber,
  body: OnboardingRiStepBody,
): Promise<OnboardingRiStepResponseDto> {
  const path = `/api/v1/onboarding/ri/sessions/${encodeURIComponent(sessionId)}/steps/${String(
    stepNumber,
  )}`;
  const { response } = await postJson(path, body);
  return response as OnboardingRiStepResponseDto;
}

export { OnboardingApiError };
